import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import dayjs from "dayjs"
import * as yup from "yup"
import { Formik, Form } from "formik"
import { Modal, ModalBody } from "reactstrap"
import { toast, Bounce } from "react-toastify"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown, {
  toSingleFilterValue,
} from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import { ICONS } from "../../../constants/icons"
import { get, patch, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { ADMIN_REQUEST_TAB_PERMISSION_MODULES } from "../../../config/adminPermissionRegistry"
import { toastApiError } from "../../../helpers/apiError"
import { filterAdminRequestTabs } from "../../../helpers/adminPermissions"
import {
  formatDisplayName,
  getUserFullName,
} from "../../../helpers/userDisplay"
import useAdminPermission from "../../../hooks/useAdminPermission"
import useAdminCityFilterOptions from "../../../hooks/useAdminCityFilterOptions"
import ViewRequestModal from "./ViewRequestModal"
import {
  CITY_UPDATE_REQUEST_WITH,
  COLLABORATOR_LIMIT_WITH,
  enrichRequestRow,
  mapCityUpdateRequest,
  mapCollaboratorLimitRequest,
  mapPhotographyCollaboratorRequest,
  mapRequestTypeTabCounts,
  mapReviewWriterRequest,
  REQUEST_FIXTURE_ROWS,
  REQUEST_TYPE_TABS,
  toAcceptedApiStatus,
  toPhotographyApiStatus,
} from "./adminRequestsData"

const PAGE_SIZE = 10
const REASON_MAX = 250
const REQUESTS_BASE_PATH = "/admin/requests"
const VALID_REQUEST_TABS = REQUEST_TYPE_TABS.map(tab => tab.key)
const DEFAULT_REQUEST_TAB = REQUEST_TYPE_TABS[0]?.key || "collaborator_limits"

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  dismissed: "Rejected",
  withdrawn: "Withdrawn",
}

const STATUS_TONE = {
  pending: "open",
  approved: "resolved",
  rejected: "dismissed",
  dismissed: "dismissed",
  withdrawn: "dismissed",
}

const inviteDecisionValidationSchema = yup.object().shape({
  decision_reason: yup
    .string()
    .trim()
    .max(REASON_MAX, `Reason must be at most ${REASON_MAX} characters`),
})

const collaboratorDecisionValidationSchema = (isApprove = false) =>
  yup.object().shape({
    approved_count: isApprove
      ? yup
          .number()
          .typeError("Enter a valid number")
          .integer("Must be a whole number")
          .min(1, "Must be at least 1")
          .required("Approved count is required")
      : yup.mixed().nullable(),
    decision_reason: yup
      .string()
      .trim()
      .max(REASON_MAX, `Reason must be at most ${REASON_MAX} characters`),
  })

const formatCity = city => {
  if (!city) return "--"
  const name = city.city || ""
  const country = city.country || ""
  if (name && country) return `${name}, ${country}`
  return name || country || "--"
}

const formatDate = value => {
  if (!value) return "--"
  return dayjs(value).format("MMM D, YYYY")
}

/** Single-city filter — backend convention: city_id=UUID */
const appendCityIdParam = (params, cityId) => {
  if (cityId) params.city_id = cityId
}

/** Normalize invite / request status from API. */
const normalizeRequestStatus = raw => {
  const value = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")

  if (
    !value ||
    value === "pending" ||
    value === "open" ||
    value === "requested" ||
    value === "waiting" ||
    value === "new"
  ) {
    return "pending"
  }
  if (
    value === "approved" ||
    value === "accepted" ||
    value === "resolved" ||
    value === "approve"
  ) {
    return "approved"
  }
  if (
    value === "rejected" ||
    value === "dismissed" ||
    value === "denied" ||
    value === "reject" ||
    value === "dismiss"
  ) {
    return "rejected"
  }
  if (value === "withdrawn") {
    return "withdrawn"
  }
  return value
}

const canDecideRequest = status => {
  const normalized = normalizeRequestStatus(status)
  return normalized === "pending"
}

const mapInviteRequest = item => {
  const firstName = formatDisplayName(item.first_name || "")
  const lastName = formatDisplayName(item.last_name || "")
  const name =
    getUserFullName({
      first_name: firstName,
      last_name: lastName,
      email: item.email,
    }) ||
    item.email ||
    "Unknown"

  return enrichRequestRow({
    id: item.id,
    requestType: "join_platform",
    name,
    firstName: firstName || "--",
    lastName: lastName || "--",
    email: item.email || "",
    avatar: item.avatar || item.profile_photo || "",
    city: formatCity(item.city),
    cityId: item.city_id || item.city?.id || "",
    projectName: "--",
    reason: item.reason || "--",
    date: formatDate(item.created_at),
    status: normalizeRequestStatus(
      item.status ?? item.state ?? item.request_status,
    ),
    currentLimit: "--",
    requestedLimit: "--",
    decisionReason: item.decision_reason || "",
    handlerName: getUserFullName(item.handler),
    handledAt: item.handled_at ? formatDate(item.handled_at) : "",
  })
}

const DecisionModal = ({ isOpen, action, request, onClose, onSuccess }) => {
  const isApprove = action === "approve"
  const isCollaboratorLimit = request?.requestType === "collaborator_limits"
  const isChangeCity = request?.requestType === "change_city"
  const title = isApprove ? "Approve Request" : "Reject Request"
  const confirmLabel = isApprove ? "Approve" : "Reject"
  const confirmVariant = isApprove ? "primary" : "danger"
  const defaultApprovedCount = Number(request?.requestedLimit) || 1

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!request?.id) return
    try {
      let response

      if (isCollaboratorLimit) {
        const payload = {
          status: isApprove ? "approved" : "rejected",
          action_reason: values.decision_reason?.trim() || "",
        }
        if (isApprove) {
          payload.approved_count = Number(values.approved_count)
        }
        response = await patch(
          endpoints.updateAdminCollaboratorLimit(request.id),
          payload,
        )
      } else if (isChangeCity) {
        response = await patch(
          endpoints.updateAdminCityUpdateRequest(request.id),
          {
            status: isApprove ? "approved" : "rejected",
            action_reason: values.decision_reason?.trim() || "",
          },
        )
      } else if (request?.requestType === "photographers") {
        response = await patch(
          endpoints.updateAdminPhotographyCollaboratorStatus(request.id),
          {
            status: isApprove ? "accepted" : "rejected",
            action_reason: values.decision_reason?.trim() || "",
          },
        )
      } else if (request?.requestType === "reviewers") {
        response = await patch(
          endpoints.updateAdminReviewWriterRequestStatus(request.id),
          {
            status: isApprove ? "accepted" : "rejected",
            action_reason: values.decision_reason?.trim() || "",
          },
        )
      } else {
        response = await post(endpoints.decideAdminInviteRequest(request.id), {
          approved: isApprove,
          decision_reason: values.decision_reason?.trim() || "",
        })
      }

      if (response?.data?.success) {
        toast.success(
          response.data.message ||
            (isApprove
              ? "Request approved successfully."
              : "Request rejected successfully."),
          {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          },
        )
        onSuccess?.()
        onClose()
      } else {
        toastApiError(response, "Failed to update request")
      }
    } catch (err) {
      toastApiError(err, "Failed to update request")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--admin-decision"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-decision-modal__body">
        <Formik
          key={isOpen ? `${action}-${request?.id}` : "closed"}
          initialValues={{
            decision_reason: "",
            approved_count: defaultApprovedCount,
          }}
          validationSchema={
            isCollaboratorLimit
              ? collaboratorDecisionValidationSchema(isApprove)
              : inviteDecisionValidationSchema
          }
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="admin-decision-modal">
              <div className="admin-decision-modal__main">
                <h3 className="admin-decision-modal__title">{title}</h3>
                {request?.name ? (
                  <p className="admin-decision-modal__subtitle">
                    {isApprove ? "Approving" : "Rejecting"} request from{" "}
                    <strong>{request.name}</strong>
                    {isCollaboratorLimit && request.projectName
                      ? ` for ${request.projectName}`
                      : ""}
                    {isChangeCity && request.cityChangeLabel
                      ? ` (${request.cityChangeLabel})`
                      : ""}
                  </p>
                ) : null}

                {isCollaboratorLimit && isApprove ? (
                  <CustomInput
                    label="Approved Limit"
                    name="approved_count"
                    type="number"
                    min={1}
                    step={1}
                    placeholder="Enter approved collaborator count"
                    value={values.approved_count}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("approved_count", true)}
                    error={touched.approved_count && errors.approved_count}
                    helperText={
                      request?.requestedLimit
                        ? `Requested: ${request.requestedLimit}`
                        : undefined
                    }
                  />
                ) : null}

                <CustomInput
                  label="Reason (optional)"
                  name="decision_reason"
                  textarea
                  rows={4}
                  placeholder="Add a reason for this decision..."
                  value={values.decision_reason}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("decision_reason", true)}
                  error={touched.decision_reason && errors.decision_reason}
                  helperText={`${values.decision_reason.length}/${REASON_MAX}`}
                />
              </div>

              <div className="admin-decision-modal__actions">
                <CustomButton
                  variant="secondary"
                  size="md"
                  type="button"
                  className="admin-decision-modal__cancel"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant={confirmVariant}
                  size="md"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className={
                    isApprove
                      ? "admin-decision-modal__confirm admin-decision-modal__confirm--approve"
                      : "admin-decision-modal__confirm admin-decision-modal__confirm--dismiss"
                  }
                >
                  {confirmLabel}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

/**
 * Admin Requests listing — Figma (`40005445:63013`).
 * Tabs are URL-addressable: `/admin/requests/:tab`.
 */
const Requests = () => {
  const { tab: tabParam } = useParams()
  const { permissionState, isReady, can } = useAdminPermission()
  const { cityOptions } = useAdminCityFilterOptions()
  const visibleRequestTabs = useMemo(
    () => filterAdminRequestTabs(permissionState, REQUEST_TYPE_TABS),
    [permissionState],
  )
  const defaultTab =
    visibleRequestTabs[0]?.key || DEFAULT_REQUEST_TAB
  const activeTab = VALID_REQUEST_TABS.includes(tabParam)
    ? tabParam
    : defaultTab
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [cityFilter, setCityFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [fixtureRows, setFixtureRows] = useState(() =>
    REQUEST_FIXTURE_ROWS.map(enrichRequestRow),
  )
  const [apiRows, setApiRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [decisionModal, setDecisionModal] = useState({
    isOpen: false,
    action: null,
    request: null,
  })
  const [viewModal, setViewModal] = useState({
    isOpen: false,
    request: null,
  })
  const [approvingId, setApprovingId] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const [tabCounts, setTabCounts] = useState(() => mapRequestTypeTabCounts())
  const [trackedTab, setTrackedTab] = useState(activeTab)

  // Keep list state in sync when the URL tab changes (same render cycle).
  if (trackedTab !== activeTab) {
    setTrackedTab(activeTab)
    setCurrentPage(1)
    setError(null)
    setApiRows([])
    setTotalItems(0)
  }

  const isJoinPlatformTab = activeTab === "join_platform"
  const isCollaboratorLimitsTab = activeTab === "collaborator_limits"
  const isChangeCityTab = activeTab === "change_city"
  const isPhotographersTab = activeTab === "photographers"
  const isReviewersTab = activeTab === "reviewers"
  const requestModule = ADMIN_REQUEST_TAB_PERMISSION_MODULES[activeTab]
  const canUpdateRequest = Boolean(requestModule && can(requestModule, "edit"))
  const isApiTab =
    isJoinPlatformTab ||
    isCollaboratorLimitsTab ||
    isChangeCityTab ||
    isPhotographersTab ||
    isReviewersTab

  const tabRedirectTo = useMemo(() => {
    if (!tabParam) {
      return `${REQUESTS_BASE_PATH}/${defaultTab}`
    }
    if (!VALID_REQUEST_TABS.includes(tabParam)) {
      return `${REQUESTS_BASE_PATH}/${defaultTab}`
    }
    if (
      isReady &&
      visibleRequestTabs.length > 0 &&
      !visibleRequestTabs.some(tab => tab.key === tabParam)
    ) {
      return `${REQUESTS_BASE_PATH}/${defaultTab}`
    }
    return null
  }, [defaultTab, isReady, tabParam, visibleRequestTabs])

  const cityLabelById = useMemo(
    () =>
      cityOptions.reduce((acc, option) => {
        acc[option.value] = option.label
        return acc
      }, {}),
    [cityOptions],
  )

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (!cityOptions.length || !cityFilter) return
    const allowed = new Set(cityOptions.map(option => String(option.value)))
    if (!allowed.has(String(cityFilter))) setCityFilter("")
  }, [cityFilter, cityOptions])

  const fetchRequestCounts = useCallback(async () => {
    try {
      // Summary counts are global — do not pass city_id.
      const response = await get(endpoints.getAdminRequestsCounts, {
        skipErrorToast: true,
      })
      if (response?.data?.success) {
        setTabCounts(mapRequestTypeTabCounts(response.data.result || {}))
      }
    } catch {
      // Keep previous tab counts; list fetch surfaces errors.
    }
  }, [])

  useEffect(() => {
    fetchRequestCounts()
  }, [fetchRequestCounts])

  const fetchJoinPlatformRequests = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        per_page: PAGE_SIZE,
      }
      if (debouncedSearch) {
        params.search = debouncedSearch
      }
      if (statusFilter) {
        params.status = statusFilter
      }
      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.getAdminInviteRequests, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const rows = (result.data || []).map(mapInviteRequest)
        setApiRows(rows)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) {
          setCurrentPage(totalPages)
        }
      } else {
        const message = toastApiError(
          response,
          "Failed to load invite requests",
        )
        setError(message)
        setApiRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load invite requests")
      setError(message)
      setApiRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [cityFilter, currentPage, debouncedSearch, statusFilter])

  const fetchCollaboratorLimitRequests = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        sort_by: "created_at",
        sort_direction: "desc",
        "with[]": COLLABORATOR_LIMIT_WITH,
        search: debouncedSearch || "",
        status: "",
      }

      if (statusFilter) {
        params.status = statusFilter
      }

      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.getAdminCollaboratorLimits, {
        params,
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const rows = (result.data || []).map(mapCollaboratorLimitRequest)

        setApiRows(rows)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) {
          setCurrentPage(totalPages)
        }
      } else {
        const message = toastApiError(
          response,
          "Failed to load collaborator limit requests",
        )
        setError(message)
        setApiRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(
        err,
        "Failed to load collaborator limit requests",
      )
      setError(message)
      setApiRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [cityFilter, currentPage, debouncedSearch, statusFilter])

  const fetchCityUpdateRequests = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        "with[]": CITY_UPDATE_REQUEST_WITH,
        search: debouncedSearch || "",
        status: "",
      }

      if (statusFilter) {
        params.status = statusFilter
      }

      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.getAdminCityUpdateRequests, {
        params,
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const rows = (result.data || []).map(mapCityUpdateRequest)

        setApiRows(rows)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) {
          setCurrentPage(totalPages)
        }
      } else {
        const message = toastApiError(
          response,
          "Failed to load city update requests",
        )
        setError(message)
        setApiRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load city update requests")
      setError(message)
      setApiRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [cityFilter, currentPage, debouncedSearch, statusFilter])

  const fetchPhotographyCollaboratorRequests = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        search: debouncedSearch || "",
        status: "",
        "with[]": ["user", "city"],
      }

      if (statusFilter) {
        params.status = toPhotographyApiStatus(statusFilter)
      }

      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.getAdminPhotographyCollaborators, {
        params,
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const rows = (result.data || []).map(item =>
          mapPhotographyCollaboratorRequest(item, cityLabelById),
        )

        setApiRows(rows)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) {
          setCurrentPage(totalPages)
        }
      } else {
        const message = toastApiError(
          response,
          "Failed to load photography requests",
        )
        setError(message)
        setApiRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load photography requests")
      setError(message)
      setApiRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [cityFilter, cityLabelById, currentPage, debouncedSearch, statusFilter])

  const fetchReviewWriterRequests = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        search: debouncedSearch || "",
        status: "",
        "with[]": ["user", "city"],
      }

      if (statusFilter) {
        params.status = toAcceptedApiStatus(statusFilter)
      }

      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.getAdminReviewWriterRequests, {
        params,
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const rows = (result.data || []).map(item =>
          mapReviewWriterRequest(item, cityLabelById),
        )

        setApiRows(rows)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) {
          setCurrentPage(totalPages)
        }
      } else {
        const message = toastApiError(
          response,
          "Failed to load review writer requests",
        )
        setError(message)
        setApiRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(
        err,
        "Failed to load review writer requests",
      )
      setError(message)
      setApiRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [cityFilter, cityLabelById, currentPage, debouncedSearch, statusFilter])

  const refetchActiveApiTab = useCallback(async () => {
    await fetchRequestCounts()
    if (isJoinPlatformTab) return fetchJoinPlatformRequests()
    if (isCollaboratorLimitsTab) return fetchCollaboratorLimitRequests()
    if (isChangeCityTab) return fetchCityUpdateRequests()
    if (isPhotographersTab) return fetchPhotographyCollaboratorRequests()
    if (isReviewersTab) return fetchReviewWriterRequests()
    return undefined
  }, [
    fetchCityUpdateRequests,
    fetchCollaboratorLimitRequests,
    fetchJoinPlatformRequests,
    fetchPhotographyCollaboratorRequests,
    fetchRequestCounts,
    fetchReviewWriterRequests,
    isChangeCityTab,
    isCollaboratorLimitsTab,
    isJoinPlatformTab,
    isPhotographersTab,
    isReviewersTab,
  ])

  useEffect(() => {
    if (isJoinPlatformTab) {
      fetchJoinPlatformRequests()
      return undefined
    }
    if (isCollaboratorLimitsTab) {
      fetchCollaboratorLimitRequests()
      return undefined
    }
    if (isChangeCityTab) {
      fetchCityUpdateRequests()
      return undefined
    }
    if (isPhotographersTab) {
      fetchPhotographyCollaboratorRequests()
      return undefined
    }
    if (isReviewersTab) {
      fetchReviewWriterRequests()
      return undefined
    }
    setApiRows([])
    setTotalItems(0)
    setError(null)
    setIsLoading(false)
    return undefined
  }, [
    fetchCityUpdateRequests,
    fetchCollaboratorLimitRequests,
    fetchJoinPlatformRequests,
    fetchPhotographyCollaboratorRequests,
    fetchReviewWriterRequests,
    isChangeCityTab,
    isCollaboratorLimitsTab,
    isJoinPlatformTab,
    isPhotographersTab,
    isReviewersTab,
  ])

  const tabItems = useMemo(
    () =>
      visibleRequestTabs.map(tab => ({
        ...tab,
        count: tabCounts[tab.key] || 0,
        to: `${REQUESTS_BASE_PATH}/${tab.key}`,
      })),
    [tabCounts, visibleRequestTabs],
  )

  const filteredFixtureRows = useMemo(() => {
    if (isApiTab) return []
    const query = debouncedSearch.toLowerCase()
    return fixtureRows.filter(row => {
      if (row.requestType !== activeTab) return false
      if (statusFilter && normalizeRequestStatus(row.status) !== statusFilter) {
        return false
      }
      if (cityFilter) {
        const cityLabel = cityOptions.find(
          option => String(option.value) === String(cityFilter),
        )?.label
        if (cityLabel && row.city !== cityLabel) {
          return false
        }
      }
      if (!query) return true
      return (
        row.name.toLowerCase().includes(query) ||
        row.city.toLowerCase().includes(query) ||
        row.projectName.toLowerCase().includes(query) ||
        row.reason.toLowerCase().includes(query)
      )
    })
  }, [
    activeTab,
    cityFilter,
    cityOptions,
    debouncedSearch,
    fixtureRows,
    isApiTab,
    statusFilter,
  ])

  const fixturePageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredFixtureRows.slice(start, start + PAGE_SIZE)
  }, [currentPage, filteredFixtureRows])

  const requests = isApiTab ? apiRows : fixturePageRows
  const listTotalItems = isApiTab ? totalItems : filteredFixtureRows.length
  const listPageSize = isApiTab ? pageSize : PAGE_SIZE

  useEffect(() => {
    if (isApiTab) return undefined
    const totalPages = Math.max(
      1,
      Math.ceil(filteredFixtureRows.length / PAGE_SIZE),
    )
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
    return undefined
  }, [currentPage, filteredFixtureRows.length, isApiTab])

  const updateFixtureStatus = (id, status) => {
    setFixtureRows(prev =>
      prev.map(row =>
        row.id === id
          ? enrichRequestRow({
              ...row,
              status,
              statusLabel: STATUS_LABEL[status],
              statusTone: STATUS_TONE[status],
            })
          : row,
      ),
    )
  }

  const openDecisionModal = (action, request) => {
    setViewModal({ isOpen: false, request: null })
    setDecisionModal({ isOpen: true, action, request })
  }

  const closeDecisionModal = () => {
    setDecisionModal({ isOpen: false, action: null, request: null })
  }

  const openViewModal = request => {
    setViewModal({ isOpen: true, request })
  }

  const closeViewModal = () => {
    setViewModal({ isOpen: false, request: null })
  }

  const handleApproveRequest = async request => {
    if (!request?.id || approvingId) return false

    if (
      request.requestType === "collaborator_limits" ||
      request.requestType === "change_city" ||
      request.requestType === "photographers" ||
      request.requestType === "reviewers"
    ) {
      openDecisionModal("approve", request)
      return false
    }

    if (request.requestType !== "join_platform") {
      updateFixtureStatus(request.id, "approved")
      toast.success("Request approved.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      closeViewModal()
      return true
    }

    setApprovingId(request.id)
    try {
      const response = await post(
        endpoints.decideAdminInviteRequest(request.id),
        {
          approved: true,
          decision_reason: "",
        },
      )

      if (response?.data?.success) {
        toast.success(
          response.data.message || "Request approved successfully.",
          {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          },
        )
        closeViewModal()
        await fetchJoinPlatformRequests()
        await fetchRequestCounts()
        return true
      }

      toastApiError(response, "Failed to approve invite request")
      return false
    } catch (err) {
      toastApiError(err, "Failed to approve invite request")
      return false
    } finally {
      setApprovingId(null)
    }
  }

  const handleDismissRequest = request => {
    if (!request?.id) return

    if (
      request.requestType === "join_platform" ||
      request.requestType === "collaborator_limits" ||
      request.requestType === "change_city" ||
      request.requestType === "photographers" ||
      request.requestType === "reviewers"
    ) {
      openDecisionModal("dismiss", request)
      return
    }

    updateFixtureStatus(request.id, "rejected")
    toast.success("Request rejected.", {
      position: "bottom-center",
      theme: "colored",
      transition: Bounce,
    })
    closeViewModal()
  }

  const downloadExportBlob = (response, fallbackFilename, successMessage) => {
    const contentType = String(
      response.headers?.["content-type"] || "",
    ).toLowerCase()

    if (contentType.includes("application/json")) {
      return response.data.text().then(text => {
        let message = "Failed to export requests"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default message
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
      })
    }

    const disposition = response.headers?.["content-disposition"] || ""
    const filenameMatch = disposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
    )
    const filename = filenameMatch?.[1]
      ? filenameMatch[1].replace(/['"]/g, "")
      : fallbackFilename

    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)

    toast.success(successMessage, {
      position: "bottom-center",
      theme: "colored",
      transition: Bounce,
    })
    return undefined
  }

  const handleExportJoinPlatform = async () => {
    if (!isJoinPlatformTab || isExporting) return

    setIsExporting(true)
    try {
      const params = {}
      if (debouncedSearch) params.search = debouncedSearch
      if (statusFilter) {
        params.status = statusFilter
      }
      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.exportAdminInviteRequests, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export invite requests")
        return
      }

      await downloadExportBlob(
        response,
        `invite-requests-${dayjs().format("YYYY-MM-DD")}.csv`,
        "Invite requests exported.",
      )
    } catch (err) {
      toastApiError(err, "Failed to export invite requests")
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportCollaboratorLimits = async () => {
    if (!isCollaboratorLimitsTab || isExporting) return

    setIsExporting(true)
    try {
      const params = {
        search: debouncedSearch || "",
        status: "",
      }
      if (statusFilter) {
        params.status = statusFilter
      }

      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.exportAdminCollaboratorLimits, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export collaborator limit requests")
        return
      }

      await downloadExportBlob(
        response,
        `collaborator-limits-${dayjs().format("YYYY-MM-DD")}.csv`,
        "Collaborator limit requests exported.",
      )
    } catch (err) {
      toastApiError(err, "Failed to export collaborator limit requests")
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportCityUpdateRequests = async () => {
    if (!isChangeCityTab || isExporting) return

    setIsExporting(true)
    try {
      const params = {
        search: debouncedSearch || "",
        status: "",
      }
      if (statusFilter) {
        params.status = statusFilter
      }
      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.exportAdminCityUpdateRequests, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export city update requests")
        return
      }

      await downloadExportBlob(
        response,
        `city-update-requests-${dayjs().format("YYYY-MM-DD")}.csv`,
        "City update requests exported.",
      )
    } catch (err) {
      toastApiError(err, "Failed to export city update requests")
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportPhotographyCollaborators = async () => {
    if (!isPhotographersTab || isExporting) return

    setIsExporting(true)
    try {
      const params = {
        search: debouncedSearch || "",
        status: "",
      }
      if (statusFilter) {
        params.status = toPhotographyApiStatus(statusFilter)
      }
      appendCityIdParam(params, cityFilter)

      const response = await get(
        endpoints.exportAdminPhotographyCollaborators,
        {
          params,
          responseType: "blob",
          skipErrorToast: true,
        },
      )

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export photography requests")
        return
      }

      await downloadExportBlob(
        response,
        `photography-collaborators-${dayjs().format("YYYY-MM-DD")}.csv`,
        "Photography requests exported.",
      )
    } catch (err) {
      toastApiError(err, "Failed to export photography requests")
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportReviewWriterRequests = async () => {
    if (!isReviewersTab || isExporting) return

    setIsExporting(true)
    try {
      const params = {
        search: debouncedSearch || "",
        status: "",
      }
      if (statusFilter) {
        params.status = toAcceptedApiStatus(statusFilter)
      }
      appendCityIdParam(params, cityFilter)

      const response = await get(endpoints.exportAdminReviewWriterRequests, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export review writer requests")
        return
      }

      await downloadExportBlob(
        response,
        `review-writer-requests-${dayjs().format("YYYY-MM-DD")}.csv`,
        "Review writer requests exported.",
      )
    } catch (err) {
      toastApiError(err, "Failed to export review writer requests")
    } finally {
      setIsExporting(false)
    }
  }

  const handleExport = isCollaboratorLimitsTab
    ? handleExportCollaboratorLimits
    : isChangeCityTab
      ? handleExportCityUpdateRequests
      : isPhotographersTab
        ? handleExportPhotographyCollaborators
        : isReviewersTab
          ? handleExportReviewWriterRequests
          : isJoinPlatformTab
            ? handleExportJoinPlatform
            : undefined

  const showLimitColumns = activeTab === "collaborator_limits"

  if (tabRedirectTo) {
    return <Navigate to={tabRedirectTo} replace />
  }

  return (
    <div className="admin-page admin-requests">
      <div className="admin-requests__toolbar">
        <h1 className="admin-requests__title">Requests</h1>
        <div className="admin-requests__controls">
          <SearchInput
            value={search}
            onChange={event => {
              setSearch(event.target.value)
              setCurrentPage(1)
            }}
            placeholder="Search by Content..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-requests__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "status",
                type: "select",
                label: "Status",
                options: [
                  { value: "pending", label: "Pending" },
                  { value: "approved", label: "Approved" },
                  { value: "rejected", label: "Rejected" },
                  { value: "withdrawn", label: "Withdrawn" },
                ],
                value: statusFilter,
                defaultValue: "",
                onChange: next => {
                  setStatusFilter(toSingleFilterValue(next))
                  setCurrentPage(1)
                },
              },
              {
                key: "city",
                type: "select",
                label: "City",
                multiple: false,
                searchable: true,
                options: cityOptions,
                value: cityFilter,
                defaultValue: "",
                onChange: next => {
                  const value = Array.isArray(next)
                    ? String(next[0] || "")
                    : String(next || "")
                  setCityFilter(value)
                  setCurrentPage(1)
                },
              },
            ]}
          />
          <ExportLogButton
            entityLabel="requests"
            onClick={handleExport}
            loading={isApiTab && isExporting}
            disabled={isApiTab && isExporting}
          />
        </div>
      </div>

      <div className="admin-requests__tabs-row">
        <ListingTabs items={tabItems} activeKey={activeTab} />
      </div>

      <div className="admin-requests__table-wrapper">
        <div
          className={`admin-requests__table${
            showLimitColumns
              ? ""
              : isChangeCityTab
                ? " admin-requests__table--compact admin-requests__table--change-city"
                : " admin-requests__table--compact"
          }`}
          aria-busy={isLoading || undefined}
        >
          <div className="admin-requests__table-row admin-requests__table-row--head">
            <div>Requested By</div>
            <div>Email</div>
            {showLimitColumns ? (
              <>
                <div>Project Name</div>
                <div>Project&apos;s City</div>
              </>
            ) : (
              <div>User&apos;s City</div>
            )}
            <div>Reason</div>
            <div>Date Reported</div>
            <div>Status</div>
            {showLimitColumns ? (
              <>
                <div>Current Limit</div>
                <div>Requested Limit</div>
              </>
            ) : null}
            <div className="admin-requests__actions-head">Actions</div>
          </div>

          {isLoading &&
            Array.from({ length: 5 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="admin-requests__table-row admin-requests__table-row--skeleton"
              >
                <div className="admin-requests__skeleton" />
                <div className="admin-requests__skeleton" />
                {showLimitColumns ? (
                  <>
                    <div className="admin-requests__skeleton" />
                    <div className="admin-requests__skeleton" />
                  </>
                ) : (
                  <div className="admin-requests__skeleton" />
                )}
                <div className="admin-requests__skeleton" />
                <div className="admin-requests__skeleton" />
                <div className="admin-requests__skeleton" />
                {showLimitColumns ? (
                  <>
                    <div className="admin-requests__skeleton" />
                    <div className="admin-requests__skeleton" />
                  </>
                ) : null}
                <div className="admin-requests__skeleton" />
              </div>
            ))}

          {!isLoading &&
            requests.map(row => (
              <div key={row.id} className="admin-requests__table-row">
                <div className="admin-requests__user">
                  <Avatar
                    src={row.avatar}
                    name={row.name}
                    size="sm"
                    className="admin-requests__avatar"
                  />
                  <TruncatedText className="admin-requests__name">
                    {row.name}
                  </TruncatedText>
                </div>

                <TruncatedText
                  as="div"
                  className="admin-requests__cell admin-requests__cell--email"
                  title={row.email || undefined}
                >
                  {row.email || "--"}
                </TruncatedText>

                {showLimitColumns ? (
                  <TruncatedText
                    as="div"
                    className="admin-requests__cell admin-requests__cell--project"
                  >
                    {row.projectName}
                  </TruncatedText>
                ) : null}

                <div className="admin-requests__city-cell">
                  <TruncatedText
                    className="admin-requests__city"
                    title={
                      row.requestType === "change_city"
                        ? row.cityChangeLabel || row.city
                        : row.city
                    }
                  >
                    {row.requestType === "change_city"
                      ? row.cityChangeLabel || row.city
                      : row.city}
                  </TruncatedText>
                </div>

                <TruncatedText
                  as="div"
                  className="admin-requests__cell admin-requests__cell--reason"
                  title={row.reason}
                >
                  {row.reason}
                </TruncatedText>

                <div className="admin-requests__cell admin-requests__cell--date">
                  {row.date}
                </div>

                <div className="admin-requests__status-cell">
                  <span
                    className={`admin-requests__status admin-requests__status--${row.statusTone}`}
                  >
                    {row.statusLabel}
                  </span>
                </div>

                {showLimitColumns ? (
                  <>
                    <div className="admin-requests__cell admin-requests__cell--limit">
                      {row.currentLimit}
                    </div>
                    <div className="admin-requests__cell admin-requests__cell--limit">
                      {row.requestedLimit}
                    </div>
                  </>
                ) : null}

                <div className="admin-requests__actions">
                  {canUpdateRequest && canDecideRequest(row.status) ? (
                    <>
                      <CustomButton
                        variant="toned"
                        tone="darkgreen"
                        size="sm"
                        className="admin-requests__btn admin-requests__btn--approve"
                        onClick={() => handleApproveRequest(row)}
                        loading={approvingId === row.id}
                        disabled={approvingId === row.id}
                      >
                        Accept
                      </CustomButton>
                      <CustomButton
                        variant="danger"
                        size="sm"
                        className="admin-requests__btn admin-requests__btn--dismiss"
                        onClick={() => handleDismissRequest(row)}
                        disabled={approvingId === row.id}
                      >
                        Reject
                      </CustomButton>
                    </>
                  ) : null}
                  <CustomIconButton
                    iconSrc={ICONS.adminEye}
                    label={`View ${row.name}`}
                    className="admin-requests__view-btn"
                    onClick={() => openViewModal(row)}
                  />
                </div>
              </div>
            ))}

          {!isLoading && !error && requests.length === 0 && (
            <div className="admin-requests__empty">
              <EmptyState
                title="No requests found"
                description="Requests will show up here once submitted."
              />
            </div>
          )}

          {!isLoading && error && requests.length === 0 && (
            <div className="admin-requests__empty">
              <EmptyState
                title="Unable to load requests"
                description={error}
                actionText="Try again"
                onActionClick={refetchActiveApiTab}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={listTotalItems}
        pageSize={listPageSize}
        onPageChange={setCurrentPage}
        itemLabel="Requests"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-requests__pagination"
      />

      <DecisionModal
        isOpen={decisionModal.isOpen}
        action={decisionModal.action}
        request={decisionModal.request}
        onClose={closeDecisionModal}
        onSuccess={refetchActiveApiTab}
      />

      <ViewRequestModal
        isOpen={viewModal.isOpen}
        request={viewModal.request}
        onClose={closeViewModal}
        onAccept={handleApproveRequest}
        onDismiss={handleDismissRequest}
        allowDecision={canUpdateRequest}
      />
    </div>
  )
}

export default Requests
